import React from 'react';
import styled from 'styled-components';
import { COLORS } from '../settings/variables/Colors';
import { mergeClassNames } from '../../utils/mergeClassNames';

export interface SegmentedOption<T extends string = string> {
  id: T;
  label: React.ReactNode;
  disabled?: boolean;
  selectedColor?: string;
  selectedTextColor?: string;
  hoverColor?: string;
  title?: string;
}

interface TrueImpactSegmentedProps<T extends string = string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (id: T) => void;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}

const Wrapper = styled.div`
  display: inline-flex;
  border: 1px solid ${COLORS.greyLight};
  border-radius: 8px;
  overflow: hidden;
  background: ${COLORS.whiteBase};
  flex-shrink: 0;

  .segmented-option {
    background: transparent;
    border: none;
    padding: 6px 12px;
    font-weight: 600;
    font-size: 12px;
    color: ${COLORS.grayDarkest};
    letter-spacing: 0.3px;
    min-width: 68px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  .segmented-option + .segmented-option {
    border-left: 1px solid ${COLORS.greyLight};
  }

  .segmented-option:not(.selected):not(.disabled):hover {
    background: var(--segmented-hover-bg, ${COLORS.grayLight});
  }

  .segmented-option.selected {
    cursor: default;
    background: var(--segmented-selected-bg, ${COLORS.grayLight});
    color: var(--segmented-selected-fg, ${COLORS.grayDarkest});
  }

  .segmented-option.disabled {
    cursor: not-allowed;
  }

  .segmented-option.disabled:not(.selected) {
    opacity: 0.6;
  }
`;

function TrueImpactSegmented<T extends string = string>({
  options,
  value,
  onChange,
  disabled,
  ariaLabel,
  className,
}: TrueImpactSegmentedProps<T>) {
  return (
    <Wrapper role="radiogroup" aria-label={ariaLabel} className={className}>
      {options.map(option => {
        const selected = option.id === value;
        const optionDisabled = Boolean(disabled || option.disabled);
        const handleClick = () => {
          if (selected || optionDisabled) return;
          onChange(option.id);
        };
        const styleVars = {
          ['--segmented-selected-bg' as string]:
            option.selectedColor || COLORS.grayLight,
          ['--segmented-selected-fg' as string]:
            option.selectedTextColor || COLORS.grayDarkest,
          ['--segmented-hover-bg' as string]:
            option.hoverColor || COLORS.grayLight,
        } as React.CSSProperties;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-disabled={optionDisabled || undefined}
            title={option.title}
            className={mergeClassNames(
              'segmented-option',
              selected && 'selected',
              optionDisabled && 'disabled',
            )}
            style={styleVars}
            onClick={handleClick}
          >
            {option.label}
          </button>
        );
      })}
    </Wrapper>
  );
}

export default TrueImpactSegmented;
